// ── ManifestViewPanel — Right panel: manifest preview (tree / SQL) ─────────
import React, { useCallback } from 'react';
import {
  Download,
  ArrowRightLeft,
  Check,
  Copy,
  Code,
  X,
  Users,
  Filter,
  FileJson,
  Upload,
} from 'lucide-react';
import type {
  ScopeManifest,
  ImportResult,
  ManifestOutcome,
} from '../types';
import type { IngestResult } from '../utils';
import { StatBadge } from './shared';
import { ManifestScopeTree } from './ManifestScopeTree';
import { useManifestDragDrop } from '../hooks/useManifestDragDrop';

interface ManifestViewPanelProps {
  manifest: ScopeManifest | null;
  cloneResult: ImportResult | null;
  setCloneResult: (v: ImportResult | null) => void;
  targetTenantId: number | '';

  // Header actions
  onDownload: () => void;
  onClear: () => void;

  // View tab
  viewTab: 'tree' | 'sql';
  setViewTab: (v: 'tree' | 'sql') => void;

  // SQL
  generatedSQL: string;
  sqlCopied: boolean;
  onCopySQL: () => void;
  onDownloadSQL: () => void;

  // Manifest tree selection
  manifestSelection: Set<string>;
  setManifestSelection: React.Dispatch<React.SetStateAction<Set<string>>>;
  onToggleManifestKey: (key: string) => void;
  onToggleManifestNamespace: (ns: string, selected: boolean) => void;

  // Apply to tenant action
  onApplyToTenant: () => void;

  /** Ingest pipeline callback — from drag-drop or file input */
  onIngest: (result: IngestResult) => void;
  /** Parse error surfaced from the ingest pipeline */
  parseError?: string;
}

export function ManifestViewPanel({
  manifest,
  cloneResult,
  setCloneResult,
  targetTenantId,
  onDownload,
  onClear,
  viewTab,
  setViewTab,
  generatedSQL,
  sqlCopied,
  onCopySQL,
  onDownloadSQL,
  manifestSelection,
  setManifestSelection,
  onToggleManifestKey,
  onToggleManifestNamespace,
  onApplyToTenant,
  onIngest,
  parseError,
}: ManifestViewPanelProps) {
  // ── Drag-and-drop with blur overlay ──────────────────────────────────────
  const handleIngest = useCallback(
    (result: IngestResult) => onIngest(result),
    [onIngest],
  );
  const { isDragOver, dragHandlers } = useManifestDragDrop({
    onIngest: handleIngest,
  });

  return (
    <div className="flex-1 min-h-0 flex flex-col relative" {...dragHandlers}>
      {/* ── Blur drop-zone overlay ─────────────────────────────────────── */}
      {isDragOver && (
        <div className="absolute inset-0 z-50 flex items-center justify-center backdrop-blur-sm bg-black/60 border-2 border-dashed border-violet-500/70 rounded-md transition-all">
          <div className="flex flex-col items-center gap-3 pointer-events-none">
            <div className="w-14 h-14 rounded-full bg-violet-500/20 flex items-center justify-center ring-2 ring-violet-500/40">
              <Upload className="w-7 h-7 text-violet-400 animate-pulse" />
            </div>
            <span className="text-sm font-medium text-violet-200">
              Drop JSON manifest here
            </span>
            <span className="text-[10px] text-violet-400/70">
              .json files only — validated on drop
            </span>
          </div>
        </div>
      )}
      {/* Clone result banner */}
      {cloneResult && (
        <div className="px-3 py-2 bg-violet-950/60 border-b border-violet-800/50 flex items-center justify-between shrink-0">
          <div className="flex items-center gap-2">
            <Users className="w-3.5 h-3.5 text-violet-400" />
            <span className="text-xs text-violet-200 font-medium">
              Cloned to tenant {targetTenantId}
            </span>
            <span className="text-[10px] text-violet-400/80">
              {cloneResult.scopes_created} created ·{' '}
              {cloneResult.scopes_existing} existing ·{' '}
              {cloneResult.outcomes_written} outcomes
              {(cloneResult.outcomes_updated ?? 0) > 0
                ? ` · ${cloneResult.outcomes_updated} updated`
                : ''}
              {(cloneResult.outcomes_skipped ?? 0) > 0
                ? ` · ${cloneResult.outcomes_skipped} skipped`
                : ''}
            </span>
          </div>
          <button
            onClick={() => setCloneResult(null)}
            className="text-violet-500 hover:text-violet-300"
          >
            <X className="w-3 h-3" />
          </button>
        </div>
      )}

      {/* Header */}
      <div className="px-3 py-2 border-b border-neutral-800 pac-glass-subtle flex items-center justify-between shrink-0">
        <span className="text-sm font-medium text-neutral-200">
          {manifest ? 'Manifest Preview' : 'Export Artifacts'}
        </span>
        {manifest && (
          <div className="flex items-center gap-2">
            <button
              className="px-2 py-1 rounded border border-violet-800 text-[10px] text-violet-300 hover:border-violet-500 flex items-center gap-1"
              onClick={onApplyToTenant}
              title="Import selected scopes into another tenant"
            >
              <Users className="w-3 h-3" />
              Apply to Tenant
            </button>
            <button
              className="px-2 py-1 rounded border border-neutral-700 text-[10px] text-neutral-300 hover:border-neutral-500 flex items-center gap-1"
              onClick={onDownload}
              title="Download manifest as JSON"
            >
              <Download className="w-3 h-3" />
              JSON
            </button>
            <button
              className="px-2 py-1 rounded border border-neutral-700 text-[10px] text-neutral-300 hover:border-neutral-500"
              onClick={onClear}
            >
              Clear
            </button>
          </div>
        )}
      </div>

      {/* Content */}
      {!manifest ? (
        <div className="shrink-0 flex items-center justify-center py-6">
          <div className="text-center space-y-2">
            <ArrowRightLeft className="w-6 h-6 text-neutral-700 mx-auto" />
            <div className="text-[10px] text-neutral-500 leading-relaxed">
              Select scopes and export, or load a manifest JSON.
            </div>
            <div className="mx-auto w-40 py-2.5 rounded-lg border border-dashed border-neutral-700/70 flex flex-col items-center gap-1 text-neutral-600 hover:border-neutral-500 hover:text-neutral-400 transition-colors cursor-default">
              <FileJson className="w-4 h-4" />
              <span className="text-[9px]">Drag &amp; drop .json</span>
            </div>
            {parseError && (
              <div className="text-[10px] text-rose-400 max-w-xs mx-auto">{parseError}</div>
            )}
          </div>
        </div>
      ) : (
        <div className="flex-1 min-h-0 flex flex-col overflow-hidden">
          {/* Manifest meta */}
          <div className="px-3 py-2 border-b border-neutral-800/50 shrink-0 space-y-1">
            <div className="flex flex-wrap gap-3 text-[10px] text-neutral-400">
              <span>
                <span className="text-neutral-600">v</span>
                {manifest.version}
              </span>
              {manifest.source_env && (
                <span>
                  <span className="text-neutral-600">env:</span>{' '}
                  {manifest.source_env}
                </span>
              )}
              <span>
                <span className="text-neutral-600">exported:</span>{' '}
                {manifest.exported_at}
              </span>
              <span>
                <span className="text-neutral-600">tenant:</span>{' '}
                {manifest.tenant_id}
              </span>
              <span>
                <span className="text-neutral-600">root:</span>{' '}
                {manifest.root_scope.scope_namespace}/
                {manifest.root_scope.scope_key}
              </span>
            </div>
            <div className="flex items-center justify-between gap-2">
              <div className="flex gap-2">
                <StatBadge
                  label="scopes"
                  value={manifest.scopes?.length ?? 0}
                />
                <StatBadge
                  label="outcomes"
                  value={manifest.outcomes?.length ?? 0}
                />
                <StatBadge
                  label="handlers"
                  value={
                    new Set(
                      (manifest.outcomes ?? []).map(
                        (o: ManifestOutcome) => o.handler_key,
                      ),
                    ).size
                  }
                />
                <StatBadge
                  label="namespaces"
                  value={
                    new Set(
                      manifest.scopes.map((s) => s.scope_namespace),
                    ).size
                  }
                />
                {(manifest.hot_stream?.length ?? 0) > 0 && (
                  <StatBadge
                    label="hot rows"
                    value={manifest.hot_stream!.length}
                  />
                )}
                {(manifest.hot_archive_files?.length ?? 0) > 0 && (
                  <StatBadge
                    label="archives"
                    value={manifest.hot_archive_files!.length}
                  />
                )}
              </div>
              {/* View tab toggle */}
              <div className="flex items-center pac-glass rounded p-0.5 shrink-0">
                <button
                  onClick={() => setViewTab('tree')}
                  className={
                    'px-2 py-0.5 rounded text-[10px] flex items-center gap-1 transition-colors ' +
                    (viewTab === 'tree'
                      ? 'pac-glass text-neutral-200'
                      : 'text-neutral-500 hover:text-neutral-300')
                  }
                >
                  <ArrowRightLeft className="w-2.5 h-2.5" />
                  Tree
                </button>
                <button
                  onClick={() => setViewTab('sql')}
                  className={
                    'px-2 py-0.5 rounded text-[10px] flex items-center gap-1 transition-colors ' +
                    (viewTab === 'sql'
                      ? 'pac-glass text-neutral-200'
                      : 'text-neutral-500 hover:text-neutral-300')
                  }
                >
                  <Code className="w-2.5 h-2.5" />
                  SQL
                </button>
              </div>
            </div>
          </div>

          {viewTab === 'tree' ? (
            <div className="flex-1 min-h-0 overflow-y-auto">
              {/* Selection summary bar */}
              <div className="px-3 py-1.5 border-b border-neutral-800/40 flex items-center justify-between shrink-0 pac-glass">
                <div className="flex items-center gap-2 text-[10px]">
                  <Filter className="w-3 h-3 text-neutral-500" />
                  <span
                    className={
                      manifestSelection.size === manifest.scopes.length
                        ? 'text-neutral-500'
                        : 'text-amber-400'
                    }
                  >
                    {manifestSelection.size} of {manifest.scopes.length}{' '}
                    scopes selected
                  </span>
                </div>
                <div className="flex items-center gap-1.5">
                  <button
                    className="px-1.5 py-0.5 rounded text-[9px] text-neutral-500 hover:text-neutral-300 hover:border-neutral-600 transition-colors"
                    onClick={() => {
                      const all = new Set(
                        manifest.scopes.map(
                          (s) =>
                            `${s.scope_namespace}\x00${s.scope_key}`,
                        ),
                      );
                      setManifestSelection(all);
                    }}
                  >
                    All
                  </button>
                  <button
                    className="px-1.5 py-0.5 rounded text-[9px] text-neutral-500 hover:text-neutral-300 hover:border-neutral-600 transition-colors"
                    onClick={() => setManifestSelection(new Set())}
                  >
                    None
                  </button>
                </div>
              </div>
              <div className="px-3 py-2">
                <ManifestScopeTree
                  scopes={manifest.scopes}
                  outcomes={manifest.outcomes ?? []}
                  selectedKeys={manifestSelection}
                  onToggleKey={onToggleManifestKey}
                  onToggleNamespace={onToggleManifestNamespace}
                />
              </div>
            </div>
          ) : (
            <>
              {/* SQL view */}
              <div className="px-3 py-1.5 border-b border-neutral-800/50 flex items-center justify-between shrink-0">
                <div className="flex items-center gap-2 text-[10px] text-neutral-500">
                  <Code className="w-3 h-3" />
                  <span>Idempotent PostgreSQL</span>
                  <span className="text-neutral-700">·</span>
                  <span className="tabular-nums">
                    {generatedSQL.split('\n').length} lines
                  </span>
                </div>
                <div className="flex items-center gap-1.5">
                  <button
                    className="px-2 py-1 rounded border border-neutral-700 text-[10px] text-neutral-300 hover:border-neutral-500 flex items-center gap-1"
                    onClick={onCopySQL}
                  >
                    {sqlCopied ? (
                      <Check className="w-3 h-3 text-emerald-400" />
                    ) : (
                      <Copy className="w-3 h-3" />
                    )}
                    {sqlCopied ? 'Copied!' : 'Copy'}
                  </button>
                  <button
                    className="px-2 py-1 rounded border border-sky-800 text-[10px] text-sky-200 hover:border-sky-600 flex items-center gap-1"
                    onClick={onDownloadSQL}
                  >
                    <Download className="w-3 h-3" />
                    .sql
                  </button>
                </div>
              </div>
              <pre className="flex-1 min-h-0 overflow-auto p-3 text-[10px] font-mono text-neutral-400 leading-relaxed whitespace-pre select-all">
                {generatedSQL || '-- Generating SQL…'}
              </pre>
              <div className="px-3 py-2 border-t border-neutral-800 pac-glass shrink-0">
                <div className="text-[9px] text-neutral-600 space-y-0.5">
                  <div>
                    Run this SQL on the target environment's PostgreSQL
                    database.
                  </div>
                  <div className="font-mono text-neutral-700">
                    psql -h &lt;host&gt; -U &lt;user&gt; -d &lt;db&gt; -f
                    scope-migration.sql
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
